import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Loader2, CheckCircle2, ArrowRight, XCircle, Sparkles, Zap, FileText } from 'lucide-react';

const MAX_POLLS = 8;
const INTERVAL = 2000;

const CONFETTI = Array.from({ length: 14 }, (_, i) => ({
  left: `${(i * 7 + 4) % 100}%`,
  delay: `${(i % 7) * 0.12}s`,
  color: ['#10b981', '#0ea5e9', '#f59e0b', '#8b5cf6', '#ec4899'][i % 5],
}));

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('polling'); // polling | success | timeout | error
  const [plan, setPlan] = useState({ label: '', bill_credits: null, ai_credits: null });
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/subscription/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data?.activated || data?.payment_status === 'paid') {
          setPlan({
            label: data?.plan_label || (data?.plan_id || '').replace(/^\w/, (c) => c.toUpperCase()),
            bill_credits: data?.bill_credits,
            ai_credits: data?.ai_credits,
          });
          setState('success');
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setState('timeout');
          return;
        }
        setTimeout(poll, INTERVAL);
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 404) { setState('error'); return; }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) { setState('error'); return; }
        setTimeout(poll, INTERVAL);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const aiUsd = plan.ai_credits != null ? (Number(plan.ai_credits) * 0.10).toFixed(2) : null;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 relative overflow-hidden" data-testid="subscription-success-page">
      <style>{`
        @keyframes bp-confetti { 0% { transform: translateY(-30px) rotate(0); opacity: 0 } 10% { opacity: 1 } 100% { transform: translateY(105vh) rotate(540deg); opacity: 0 } }
        @keyframes bp-pop { 0% { transform: scale(0.4); opacity: 0 } 60% { transform: scale(1.08) } 100% { transform: scale(1); opacity: 1 } }
        .bp-pop { animation: bp-pop .5s cubic-bezier(.2,.8,.2,1) both }
        .bp-chip { animation: bp-pop .5s cubic-bezier(.2,.8,.2,1) both }
      `}</style>

      {state === 'success' && CONFETTI.map((c, i) => (
        <span key={i} aria-hidden style={{
          position: 'absolute', top: 0, left: c.left, width: 9, height: 9, borderRadius: 2,
          background: c.color, animation: `bp-confetti 2.6s ease-in ${c.delay} 1 forwards`,
        }} />
      ))}

      <div className="w-full max-w-md text-center relative z-10">
        <div className="inline-flex items-center gap-3 mb-10">
          <img src="/billportal-logo.png" alt="BillPortal" className="h-9 w-9" />
          <span className="text-xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>BillPortal</span>
        </div>

        {state === 'polling' && (
          <div data-testid="subscription-success-polling">
            <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-6" />
            <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>Confirming your payment…</h1>
            <p className="text-sm text-slate-500 mt-2">This only takes a moment.</p>
          </div>
        )}

        {state === 'success' && (
          <div>
            <div className="bp-pop h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-9 w-9 text-primary" />
            </div>
            <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-primary mb-2">
              <Sparkles className="h-3.5 w-3.5" /> Welcome to {plan.label}
            </p>
            <h1 className="text-3xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>You're all set! 🎉</h1>
            <p className="text-sm text-slate-600 mt-3">
              Your <b data-testid="subscription-success-plan-name">{plan.label}</b> plan is active and your credits have been auto-applied to this workspace.
            </p>

            {(plan.bill_credits != null || plan.ai_credits != null) && (
              <div className="mt-6 grid grid-cols-2 gap-3" data-testid="subscription-success-credits">
                {plan.bill_credits != null && (
                  <div className="bp-chip border border-slate-200 rounded-lg p-4" style={{ animationDelay: '.15s' }} data-testid="success-bill-credits">
                    <FileText className="h-5 w-5 text-violet-600 mx-auto mb-2" />
                    <div className="text-2xl font-black tracking-tighter">{plan.bill_credits}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">bill credits / month</div>
                  </div>
                )}
                {plan.ai_credits != null && (
                  <div className="bp-chip border border-slate-200 rounded-lg p-4" style={{ animationDelay: '.28s' }} data-testid="success-ai-credits">
                    <Zap className="h-5 w-5 text-emerald-600 mx-auto mb-2" />
                    <div className="text-2xl font-black tracking-tighter">{plan.ai_credits}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">AI credits{aiUsd ? ` ($${aiUsd})` : ''} / month</div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 space-y-2">
              <Button onClick={() => navigate('/bills')} className="w-full rounded-none h-11 font-bold uppercase tracking-wide" data-testid="subscription-success-start-btn">
                Start processing bills <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button onClick={() => navigate('/billing')} variant="outline" className="w-full rounded-none h-11 font-bold uppercase tracking-wide" data-testid="subscription-success-billing-btn">
                View billing & invoices
              </Button>
            </div>
          </div>
        )}

        {(state === 'timeout' || state === 'error') && (
          <div data-testid="subscription-success-error">
            <div className="h-16 w-16 bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-9 w-9 text-slate-500" />
            </div>
            <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {state === 'timeout' ? 'Still processing…' : 'Something went wrong'}
            </h1>
            <p className="text-sm text-slate-600 mt-3">
              {state === 'timeout'
                ? 'Your payment is taking longer than usual to confirm. Check the Billing area in a minute.'
                : 'We could not confirm this payment. If you were charged, it will reflect in Billing shortly.'}
            </p>
            <Button onClick={() => navigate('/billing')} className="w-full rounded-none h-11 font-bold uppercase tracking-wide mt-8" data-testid="subscription-success-retry-btn">
              Go to billing
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
